import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tag, Modal, Form,
  message, Tooltip, InputNumber, DatePicker, Descriptions
} from 'antd';
import {
  PlusOutlined, EditOutlined, EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Option } = Select;

interface RateMatrix {
  matrixId: number;
  name: string;
  nameEn: string;
  description: string;
  productType: string;
  location: string;
  automationLevel: 'auto' | 'semi' | 'all';
  laborRate: number;
  burdenRate: number;
  sgaRate: number;
  idlRate: number;
  scrapRate: number;
  effectiveDate: string;
  expireDate: string;
  priority: number;
  status: 'active' | 'inactive';
  createdAt: string;
}

const Rates: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState<RateMatrix[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [searchParams, setSearchParams] = useState<any>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRate, setEditingRate] = useState<RateMatrix | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRate, setSelectedRate] = useState<RateMatrix | null>(null);
  const [form] = Form.useForm();

  const fetchRates = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...searchParams
      });
      
      const res = await fetch(`/api/rates/matrix?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        setRates(data.data.list);
        setPagination({ ...pagination, page, pageSize, total: data.data.pagination.total });
      }
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, [searchParams]);

  const handleCreate = () => {
    setEditingRate(null);
    form.resetFields();
    form.setFieldsValue({
      automationLevel: 'semi',
      priority: 0,
      sgaRate: 0.15,
      idlRate: 0.08,
      scrapRate: 0.02
    });
    setModalVisible(true);
  };

  const handleEdit = (record: RateMatrix) => {
    setEditingRate(record);
    form.setFieldsValue({
      name: record.name,
      nameEn: record.nameEn,
      description: record.description,
      productType: record.productType,
      location: record.location,
      automationLevel: record.automationLevel,
      laborRate: record.laborRate,
      burdenRate: record.burdenRate,
      sgaRate: record.sgaRate,
      idlRate: record.idlRate,
      scrapRate: record.scrapRate,
      effectiveDate: record.effectiveDate ? dayjs(record.effectiveDate) : null,
      expireDate: record.expireDate ? dayjs(record.expireDate) : null,
      priority: record.priority
    });
    setModalVisible(true);
  };

  const handleView = (record: RateMatrix) => {
    setSelectedRate(record);
    setDetailVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const url = editingRate 
        ? `/api/rates/matrix/${editingRate.matrixId}`
        : '/api/rates/matrix';
      
      const res = await fetch(url, {
        method: editingRate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: values.name,
          nameEn: values.nameEn,
          description: values.description,
          productType: values.productType,
          location: values.location,
          automationLevel: values.automationLevel,
          laborRate: values.laborRate,
          burdenRate: values.burdenRate,
          sgaRate: values.sgaRate,
          idlRate: values.idlRate,
          scrapRate: values.scrapRate,
          effectiveDate: values.effectiveDate?.format('YYYY-MM-DD'),
          expireDate: values.expireDate?.format('YYYY-MM-DD'),
          priority: values.priority
        })
      });

      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        setModalVisible(false);
        fetchRates(pagination.page, pagination.pageSize);
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const columns = [
    {
      title: t('rate.name'),
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: t('project.productType'),
      dataIndex: 'productType',
      key: 'productType',
      width: 100,
      render: (v: string) => v || '-'
    },
    {
      title: t('project.location'),
      dataIndex: 'location',
      key: 'location',
      width: 120,
      render: (v: string) => v || '-'
    },
    {
      title: t('project.solutionType'),
      dataIndex: 'automationLevel',
      key: 'automationLevel',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'auto' ? 'green' : v === 'semi' ? 'blue' : 'default'}>
          {v === 'all' ? t('common.all') : t(`project.${v}`)}
        </Tag>
      )
    },
    {
      title: t('cost.laborRate'),
      dataIndex: 'laborRate',
      key: 'laborRate',
      width: 100,
      render: (v: number) => `$${v?.toFixed(2)}/h`
    },
    {
      title: t('cost.burdenRate'),
      dataIndex: 'burdenRate',
      key: 'burdenRate',
      width: 100,
      render: (v: number) => `${(v * 100).toFixed(1)}%`
    },
    {
      title: t('cost.sgaRate'),
      dataIndex: 'sgaRate',
      key: 'sgaRate',
      width: 80,
      render: (v: number) => `${(v * 100).toFixed(1)}%`
    },
    {
      title: t('rate.effectiveDate'),
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      width: 110,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('rate.expireDate'),
      dataIndex: 'expireDate',
      key: 'expireDate',
      width: 110,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('rate.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: (a: RateMatrix, b: RateMatrix) => a.priority - b.priority
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {t(`rate.${status}`)}
        </Tag>
      )
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: RateMatrix) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            style={{ width: 120 }}
            placeholder={t('project.location')}
            allowClear
            onChange={(v) => setSearchParams((prev: any) => ({ ...prev, location: v }))}
          >
            <Option value="Thailand">Thailand</Option>
            <Option value="Mexico">Mexico</Option>
            <Option value="China Haiyan">China Haiyan</Option>
            <Option value="China Haining">China Haining</Option>
          </Select>
          <Select
            style={{ width: 120 }}
            placeholder={t('common.status')}
            allowClear
            onChange={(v) => setSearchParams((prev: any) => ({ ...prev, status: v }))}
          >
            <Option value="active">{t('rate.active')}</Option>
            <Option value="inactive">{t('rate.inactive')}</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => fetchRates()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('rate.addRate')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={rates}
          rowKey="matrixId"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => fetchRates(page, pageSize)
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingRate ? t('rate.editRate') : t('rate.addRate')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('rate.name')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="nameEn" label={`${t('rate.name')} (English)`}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('project.description')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          
          <Form.Item label={t('rate.matchingRules')}>
            <Space wrap>
              <Form.Item name="productType" noStyle>
                <Input style={{ width: 120 }} placeholder={t('project.productType')} />
              </Form.Item>
              <Form.Item name="location" noStyle>
                <Select style={{ width: 140 }} placeholder={t('project.location')} allowClear>
                  <Option value="Thailand">Thailand</Option>
                  <Option value="Mexico">Mexico</Option>
                  <Option value="China Haiyan">China Haiyan</Option>
                  <Option value="China Haining">China Haining</Option>
                </Select>
              </Form.Item>
              <Form.Item name="automationLevel" noStyle>
                <Select style={{ width: 120 }}>
                  <Option value="all">{t('common.all')}</Option>
                  <Option value="auto">{t('project.auto')}</Option>
                  <Option value="semi">{t('project.semi')}</Option>
                </Select>
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label={t('cost.rateMatch')}>
            <Space wrap>
              <Form.Item name="laborRate" label={t('cost.laborRate')} rules={[{ required: true }]}>
                <InputNumber min={0} step={0.1} precision={2} addonAfter="$/h" />
              </Form.Item>
              <Form.Item name="burdenRate" label={t('cost.burdenRate')} rules={[{ required: true }]}>
                <InputNumber min={0} max={1} step={0.01} precision={4} />
              </Form.Item>
            </Space>
          </Form.Item>

          <Space wrap>
            <Form.Item name="sgaRate" label={t('cost.sgaRate')}>
              <InputNumber min={0} max={1} step={0.01} precision={4} />
            </Form.Item>
            <Form.Item name="idlRate" label={t('cost.idlRate')}>
              <InputNumber min={0} max={1} step={0.01} precision={4} />
            </Form.Item>
            <Form.Item name="scrapRate" label={t('cost.scrapRate')}>
              <InputNumber min={0} max={1} step={0.01} precision={4} />
            </Form.Item>
          </Space>

          <Space wrap>
            <Form.Item name="effectiveDate" label={t('rate.effectiveDate')}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="expireDate" label={t('rate.expireDate')}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="priority" label={t('rate.priority')}>
              <InputNumber min={0} max={100} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${t('rate.matrix')} - ${selectedRate?.name}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRate && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label={t('rate.name')}>{selectedRate.name}</Descriptions.Item>
            <Descriptions.Item label="English">{selectedRate.nameEn || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('project.productType')}>{selectedRate.productType || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('project.location')}>{selectedRate.location || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('project.solutionType')}>
              {selectedRate.automationLevel === 'all' ? t('common.all') : t(`project.${selectedRate.automationLevel}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('rate.priority')}>{selectedRate.priority}</Descriptions.Item>
            <Descriptions.Item label={t('cost.laborRate')}>${selectedRate.laborRate?.toFixed(2)}/h</Descriptions.Item>
            <Descriptions.Item label={t('cost.burdenRate')}>{(selectedRate.burdenRate * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label={t('cost.sgaRate')}>{(selectedRate.sgaRate * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label={t('cost.idlRate')}>{(selectedRate.idlRate * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label={t('cost.scrapRate')}>{(selectedRate.scrapRate * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag color={selectedRate.status === 'active' ? 'success' : 'default'}>
                {t(`rate.${selectedRate.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('rate.effectiveDate')}>
              {selectedRate.effectiveDate ? dayjs(selectedRate.effectiveDate).format('YYYY-MM-DD') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('rate.expireDate')}>
              {selectedRate.expireDate ? dayjs(selectedRate.expireDate).format('YYYY-MM-DD') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Rates;
